// eslint-disable-next-line

import {
	ApartmentOutlined,
	CalendarOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import {
	Alert,
	DatePicker,
	FloatButton,
	Input,
	Modal,
	message,
	Select,
	Table,
	Tag,
	Upload,
} from "antd";
import dayjs from "dayjs";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import DepartmentManagementModal from "../components/DepartmentManagementModal";
import WorkingDaysMasterModal from "../components/WorkingDaysMasterModal";
import BASE_URL, { COMPANY_CHOICES } from "../constants";
import AdminModalContext from "../context/AdminModalContext";
import AuthContext from "../context/AuthContext";
import { describeRequestError } from "../utils/apiErrors";
import {
	NOT_SET,
	renderNullable,
	renderNullableTag,
} from "../utils/renderNullable";
import useAxios from "../utils/useAxios";

// grade 等の任意項目はバックエンドで null=True のため未設定ユーザーが存在する。
// null に localeCompare を呼ぶと描画例外で白画面になるため、未設定は空文字として比較する
// （片側だけのガードだと localeCompare(null) が "null" 文字列比較になり順序も不正になる。
// 両側ガードの担保はテーブル経由では検証しづらいため、テストから直接検証する目的で export）
export const compareNullableText = (a, b) => (a || "").localeCompare(b || "");

// 氏名は first_name / last_name の片方が未設定でも "null null" と出さない。
// 欠けた側を落として繋ぎ、両方欠けていれば renderNullable が「-」にする。
export const buildDisplayName = (firstName, lastName) =>
	renderNullable([firstName, lastName].filter(Boolean).join(" "));

// フィルターメニューの候補も一覧セルと同じ「-」で見せる。未設定ユーザーが
// いると、値の一覧から作る候補にラベルの無い選択肢が混ざり、何を選ぶ項目か判別できない。
//
// value は onFilter の照合値（各列とも record.<field> === value、氏名のみ
// `${first_name} ${last_name}` との部分一致）なので変更しない。ここを「-」にすると
// 未設定ユーザーを絞り込めなくなるため、表示（text）だけを揃える。
export const toFilterOptions = (values) =>
	values.map((value) => ({ text: renderNullable(value), value }));

export const generateNameFilters = (data) => {
	const seen = new Map();
	for (const user of data) {
		const value = `${user.first_name} ${user.last_name}`;
		if (!seen.has(value)) {
			seen.set(value, buildDisplayName(user.first_name, user.last_name));
		}
	}
	return Array.from(seen, ([value, text]) => ({ text, value }));
};

export const generateHRCodeFilters = (data) =>
	toFilterOptions([...new Set(data.map((user) => user.hrCode))]);

// Grade / NAT Group / Company の編集用 Select は選択肢が整数値(GRADE_CHOICES 等)
// で管理される一方、ユーザーレコード側にはラベル文字列(例: "B2")で保存されている。
// 現在値をプレフィルする際にラベル文字列を対応する選択肢の value へ変換するためのヘルパー。
// 未設定(null/undefined)や選択肢に一致しない値は「未選択」として null を返す。
const findChoiceValueByLabel = (choices, label) => {
	if (label === null || label === undefined) return null;
	const match = choices.find((choice) => choice.label === label);
	return match ? match.value : null;
};

// Edit User モーダルの初期化・リセットに使う空テンプレート。
// showEditUserModal でのプレフィルと handleEditCancel/成功時のリセットで共有する。
const EMPTY_EDITED_USER_DATA = {
	grade: null,
	organizationCode: "",
	position: "",
	// dep は Department.id を持つ（保存先は実効部門 User.dep の FK）。
	// 旧・自由入力の department は書き込み対象に含めない。
	dep: null,
	natGroup: null,
	workingLocation: "",
	mobilization: "",
	company: null,
};

const Dashboard = () => {
	const { authTokens, user } = useContext(AuthContext);
	const { pendingAdminModal, clearPendingAdminModal } =
		useContext(AdminModalContext);
	const [users, setUsers] = useState([]);
	// 取得が終わるまで Table は空配列を「データなし」として描画してしまう。
	// 初期値を true にして、初回レンダー〜useEffect 発火の間も読み込み中として見せる。
	const [usersLoading, setUsersLoading] = useState(true);
	const [usersPageSize, setUsersPageSize] = useState(10);
	const [adminUserId, setAdminUserId] = useState(null);
	const [revokeAdminUserId, setRevokeAdminUserId] = useState(null);
	const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
	// アップロード中はボタンを disabled にして連打を防ぐ
	const [excelUploading, setExcelUploading] = useState(false);
	const [isWorkingDaysModalOpen, setIsWorkingDaysModalOpen] = useState(false);
	const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
	const [open, setOpen] = useState(false);
	const [openEdit, setOpenEdit] = useState(false);
	const [grade, setGrade] = useState(null);
	const [natGroup, setNatGroup] = useState(null);
	const [company, setCompany] = useState(null);
	const { Option } = Select;

	const [confirmLoading, setConfirmLoading] = useState(false);
	// Issue #69: Edit User の保存中とパスワードリセット中は、それぞれの操作を
	// 受け付けない（応答待ちの連打で同じ要求が重ねて飛ぶのを防ぐ）。state の反映は
	// 次のレンダー以降のため、同じティックの連打は同期で閉じる ref で弾く。
	const savingUserRef = useRef(false);
	// パスワードリセットの多重起動ガード（利用者 ID 単位）。
	const resetLockRef = useRef(new Set());
	const [savingUser, setSavingUser] = useState(false);
	const [resettingUserId, setResettingUserId] = useState(null);
	const [modalText, setModalText] = useState(
		// 見出しでない文を Title Case で書かない・冠詞を補う
		"Please select a month and year (the default is the current month).",
	);
	const [selectedDate, setSelectedDate] = useState(null);

	const [extractUserId, setExtractUserId] = useState(null);
	const [extractUsername, setExtractUsername] = useState(null);

	const [editUserId, setEditUserId] = useState(null);
	const [editUsername, setEditUsername] = useState("");

	const [editedUserData, setEditedUserData] = useState(EMPTY_EDITED_USER_DATA);
	// プレフィル時点の値を保持し、handleEditOk で「差分のみ」を PATCH するために使う
	const [originalUserData, setOriginalUserData] = useState(
		EMPTY_EDITED_USER_DATA,
	);
	// Edit User の Department は部門マスタ由来の Select にする（自由入力を廃止）。
	// 表記ゆれの新規発生源を断ち、マスタに無い値を保存できないようにするため。
	const [departmentChoices, setDepartmentChoices] = useState([]);

	const GRADE_CHOICES = [
		{ value: 0, label: "A1" },
		{ value: 1, label: "A2" },
		{ value: 2, label: "A3" },
		{ value: 3, label: "B1" },
		{ value: 4, label: "B2" },
		{ value: 5, label: "B3" },
		{ value: 6, label: "B4" },
		{ value: 7, label: "B5" },
	];

	const NAT_GROUP_CHOICES = [
		{ value: 0, label: "VCH" },
		{ value: 1, label: "FUD" },
		{ value: 2, label: "TD" },
		{ value: 3, label: "CWD" },
		{ value: 4, label: "PSD" },
		{ value: 5, label: "RSMD" },
	];

	// フィルタ選択肢はユーザー一覧が変わったときだけ再計算する（毎レンダー再生成を回避）
	const uniquePositions = useMemo(
		() => Array.from(new Set(users.map((user) => user.position))),
		[users],
	);

	// フィルタ選択肢は表示/onFilter/sorter と同じ user.dep(部署マスタ由来)から
	// 生成する。旧・自由入力項目の user.department から生成すると別データになり、
	// 選択肢を選んでもヒット0件になる
	const uniqueDepartments = useMemo(
		() => Array.from(new Set(users.map((user) => user.dep))),
		[users],
	);

	const workingLocations = useMemo(
		() => Array.from(new Set(users.map((user) => user.workingLocation))),
		[users],
	);

	const uniqueCompanies = useMemo(
		() => Array.from(new Set(users.map((user) => user.company))),
		[users],
	);

	const uniqueNAT = useMemo(
		() => Array.from(new Set(users.map((user) => user.natGroup))),
		[users],
	);

	const natFilters = useMemo(() => toFilterOptions(uniqueNAT), [uniqueNAT]);

	const companyFilters = useMemo(
		() => toFilterOptions(uniqueCompanies),
		[uniqueCompanies],
	);

	const workingLocationFilters = useMemo(
		() => toFilterOptions(workingLocations),
		[workingLocations],
	);

	const positionFilters = useMemo(
		() => toFilterOptions(uniquePositions),
		[uniquePositions],
	);

	const departmentFilters = useMemo(
		() => toFilterOptions(uniqueDepartments),
		[uniqueDepartments],
	);

	const handleDateChange = (date, dateString) => {
		setSelectedDate(dateString);
	};

	// これらのモーダルは destroyOnClose を持たず一度開くと mount され
	// 続けるため、autoFocus 属性では 2 回目以降の表示でフォーカスが当たらない。
	// open の変化を見て明示的に focus する。
	const extractDateRef = useRef(null);
	const editGradeRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;
		const timer = setTimeout(() => extractDateRef.current?.focus(), 0);
		return () => clearTimeout(timer);
	}, [open]);

	useEffect(() => {
		if (!openEdit) return undefined;
		const timer = setTimeout(() => editGradeRef.current?.focus(), 0);
		return () => clearTimeout(timer);
	}, [openEdit]);

	const [messageApi, contextHolder] = message.useMessage();
	// エラーは操作ごとに毎回通知する。表示を「セッション中 1 回だけ」に制限すると、
	// 一度どこかでエラーが出た後の別操作の失敗が完全に無言になる
	const showError = (errorMessage) => {
		messageApi.error(errorMessage);
	};

	const api = useAxios();

	const usersDataSource = [...users];
	const showExcelModal = () => {
		setIsExcelModalOpen(true);
	};

	const handleCancelExcel = () => {
		setIsExcelModalOpen(false);
	};

	const showExtractTsModal = (userId, username) => {
		setOpen(true);
		setModalText(
			`Please select a month and year for ${username} (the default is the current month).`,
		);
		setConfirmLoading(false);
		// 説明文は「既定は当月」と言うのにピッカーは空欄だった。
		// 開いた時点で当月を入れ、表示と実際に出力される月を一致させる。
		setSelectedDate(dayjs().format("YYYY-MM"));
		setExtractUserId(userId);
		setExtractUsername(username);
	};

	const showEditUserModal = (id, username) => {
		// 選択したユーザーの現在値でフォームを初期化する。
		// 直前に別ユーザーを編集していた場合でも、ここで確実に上書きされるため入力が残留しない
		const targetUser = users.find((u) => u.id === id);
		const prefillData = targetUser
			? {
					grade: findChoiceValueByLabel(GRADE_CHOICES, targetUser.grade),
					organizationCode: targetUser.organizationCode ?? "",
					position: targetUser.position ?? "",
					// Department は一覧の表示/フィルタ/ソートと同じ部署マスタ由来の dep を
					// プレフィルする。旧・自由入力項目の department から取ると、一覧で見て
					// いる部署と編集画面の初期値が食い違う。
					// Select の値は Department.id。表示名（dep）から id を逆引き
					// すると、正規化キーが同じ部門が複数ある移行期に別の行へ解決されうるため、
					// バックエンドが返す dep_id をそのまま使う
					dep: targetUser.dep_id ?? null,
					natGroup: findChoiceValueByLabel(
						NAT_GROUP_CHOICES,
						targetUser.natGroup,
					),
					workingLocation: targetUser.workingLocation ?? "",
					mobilization: targetUser.mobilization ?? "",
					company: findChoiceValueByLabel(COMPANY_CHOICES, targetUser.company),
				}
			: EMPTY_EDITED_USER_DATA;

		setEditedUserData(prefillData);
		setOriginalUserData(prefillData);
		setGrade(prefillData.grade);
		setNatGroup(prefillData.natGroup);
		setCompany(prefillData.company);
		setEditUsername(username ?? "");
		setEditUserId(id);
		setOpenEdit(true);
	};

	const handleTsOk = () => {
		setConfirmLoading(true);
		extract_user_timesheet(extractUserId, extractUsername);
	};

	// プレフィルされた初期値(originalUserData)との差分のみを PATCH ペイロードに含める。
	// 触っていない項目まで送信すると、他ユーザーの残留値による誤上書きが起こりうるため
	const handleEditOk = async () => {
		// Issue #69: 保存の応答を待つ間の連打で同じ更新が何度も飛ばないようにする
		if (savingUserRef.current) return;
		savingUserRef.current = true;
		setSavingUser(true);
		try {
			const changedData = Object.fromEntries(
				Object.entries(editedUserData).filter(
					([key, value]) => value !== originalUserData[key],
				),
			);

			if (Object.keys(changedData).length > 0) {
				await editUser(editUserId, changedData);
				resetEditUserState();
			} else {
				// 「更新するデータが無い」ではなく「変更が無いので保存しない」
				// ことが伝わる文言にする。
				messageApi.warning("No changes to save");
			}
		} catch (error) {
			console.error(error);
		} finally {
			savingUserRef.current = false;
			setSavingUser(false);
		}
	};

	// for_selection=1 は正規化キーが同じ部門の 2 件目以降を返さない（応急対応）。
	// 本番データの重複部門を整理するまでは、除外された側の重複部門に所属するユーザーが
	// 存在しうる。その場合 value(dep_id) に一致する選択肢が無く、antd Select は名前の代わりに
	// 生の id を表示するため、管理者が現所属を判別できないまま誤って変更しかねない。
	// 現所属だけは表示用の選択肢として補完する（フィルタ自体は変えない）。
	const departmentOptions = useMemo(() => {
		const options = departmentChoices.map(({ id, name }) => ({ id, name }));
		const currentId = editedUserData.dep;
		if (
			currentId !== null &&
			currentId !== undefined &&
			!options.some((option) => option.id === currentId)
		) {
			const currentName = users.find((u) => u.id === editUserId)?.dep;
			options.unshift({ id: currentId, name: currentName ?? `#${currentId}` });
		}
		return options;
	}, [departmentChoices, editedUserData.dep, users, editUserId]);

	// 未所属化は「所属を空にする」取り消しにくい変更なので、確認ダイアログで
	// 明示的に承認されたときだけ null を送る。クリアしてキャンセルした場合は現在の所属を保つ
	// （空欄のまま保存して黙って未所属化される事故を防ぐ）。
	const handleDepartmentChange = (value) => {
		if (value === undefined || value === null) {
			Modal.confirm({
				title: "Remove this user's department?",
				content: "The user will have no department until you assign one.",
				okText: "Remove",
				okType: "danger",
				cancelText: "Cancel",
				onOk: () => handleInputChange("dep", null),
			});
			return;
		}
		handleInputChange("dep", value);
	};

	const handleInputChange = (fieldName, value) => {
		const parsedValue = fieldName === "grade" ? parseInt(value, 10) : value;
		setEditedUserData((prevData) => ({
			...prevData,
			[fieldName]: parsedValue,
		}));
		if (fieldName === "grade") {
			setGrade(parsedValue);
		}
		if (fieldName === "natGroup") {
			setNatGroup(parsedValue);
		}
		if (fieldName === "company") {
			setCompany(parsedValue);
		}
	};

	const handleTsCancel = () => {
		setOpen(false);
		setSelectedDate(null);
	};

	// モーダルを閉じるライフサイクル(キャンセル/成功)と編集 state を結びつけ、
	// 次に別ユーザーを開いたときに前回の入力が残らないようにする
	const resetEditUserState = () => {
		setOpenEdit(false);
		setEditUserId(null);
		setEditUsername("");
		setEditedUserData(EMPTY_EDITED_USER_DATA);
		setOriginalUserData(EMPTY_EDITED_USER_DATA);
		setGrade(null);
		setNatGroup(null);
		setCompany(null);
	};

	const handleEditCancel = () => {
		resetEditUserState();
	};

	useEffect(() => {
		getUsers();
	}, []);

	// Department Select の選択肢は Edit User を開くたびに取り直す。
	// 一覧の初回ロードと同じ useEffect にまとめないのは、部門マスタが Edit User 以外では
	// 不要なうえ、開くたびに取り直せば他の管理者が追加した部門にも追従できるため。
	useEffect(() => {
		if (!openEdit) return;
		getDepartments();
	}, [openEdit]);

	// ヘッダーメニューの「Admin page」から遷移してきた場合、
	// リクエストされたモーダルを開いてリクエストをクリアする
	useEffect(() => {
		if (pendingAdminModal === "workingDays") {
			setIsWorkingDaysModalOpen(true);
			clearPendingAdminModal();
		} else if (pendingAdminModal === "excel") {
			setIsExcelModalOpen(true);
			clearPendingAdminModal();
		} else if (pendingAdminModal === "departments") {
			setIsDepartmentModalOpen(true);
			clearPendingAdminModal();
		}
	}, [pendingAdminModal, clearPendingAdminModal]);

	const nameFilters = useMemo(() => generateNameFilters(users), [users]);
	const hrCodeFilters = useMemo(() => generateHRCodeFilters(users), [users]);

	const userColumns = [
		{
			title: "HR Code",
			dataIndex: "hrCode",
			key: "hrCode",
			render: (text, record) => renderNullableTag(record.hrCode, "blue"),
			sorter: (a, b) => compareNullableText(a.hrCode, b.hrCode),
			filters: hrCodeFilters,
			filterMultiple: true,
			filterSearch: true,
			onFilter: (value, record) => record.hrCode === value,
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => {
				const nameA = `${a.first_name} ${a.last_name}`;
				const nameB = `${b.first_name} ${b.last_name}`;
				return nameA.localeCompare(nameB);
			},
			render: (text, record) => (
				<p>{buildDisplayName(record.first_name, record.last_name)}</p>
			),
			width: "15%",
			onFilter: (value, record) => {
				const fullName = `${record.first_name} ${record.last_name}`;
				return fullName.toLowerCase().includes(value.toLowerCase());
			},
			filters: nameFilters,
			filterMultiple: true,
			filterSearch: true,
		},
		{
			title: "Grade",
			dataIndex: "grade",
			key: "grade",
			render: (text, record) => renderNullableTag(record.grade, "blue"),
			filters: [
				// Grade は固定選択肢のため未設定ユーザーを絞り込めなかった。
				// 表示は一覧セルと同じ「-」に揃える。
				{ text: NOT_SET, value: null },
				{ text: "A1", value: "A1" },
				{ text: "A2", value: "A2" },
				{ text: "A3", value: "A3" },
				{ text: "B1", value: "B1" },
				{ text: "B2", value: "B2" },
				{ text: "B3", value: "B3" },
				{ text: "B4", value: "B4" },
				{ text: "B5", value: "B5" },
			],
			filterMultiple: false,
			filterSearch: true,
			onFilter: (value, record) => record.grade === value,
			sorter: (a, b) => compareNullableText(a.grade, b.grade),
		},
		{
			title: "Organization Code",
			dataIndex: "organizationCode",
			key: "organizationCode",
			render: (text, record) =>
				renderNullableTag(record.organizationCode, "blue"),
		},
		{
			title: "Position",
			dataIndex: "position",
			key: "position",
			render: (text, record) => <p>{renderNullable(record.position)}</p>,
			filters: positionFilters,
			filterMultiple: true,
			filterSearch: true,
			onFilter: (value, record) => record.position === value,
			sorter: (a, b) => compareNullableText(a.position, b.position),
		},
		{
			title: "Department",
			dataIndex: "department",
			key: "department",
			render: (text, record) => <p>{renderNullable(record.dep)}</p>,
			filters: departmentFilters,
			filterMultiple: true,
			filterSearch: true,
			onFilter: (value, record) => record.dep === value,
			sorter: (a, b) => compareNullableText(a.dep, b.dep),
		},
		{
			title: "NAT Group",
			dataIndex: "natGroup",
			key: "natGroup",
			render: (text, record) => <p>{renderNullable(record.natGroup)}</p>,
			filters: natFilters,
			filterMultiple: true,
			filterSearch: true,
			onFilter: (value, record) => record.natGroup === value,
			sorter: (a, b) => compareNullableText(a.natGroup, b.natGroup),
		},
		{
			title: "Working Location",
			dataIndex: "workingLocation",
			key: "workingLocation",
			render: (text, record) => <p>{renderNullable(record.workingLocation)}</p>,
			filters: workingLocationFilters,
			filterMultiple: true,
			filterSearch: true,
			onFilter: (value, record) => record.workingLocation === value,
			sorter: (a, b) =>
				compareNullableText(a.workingLocation, b.workingLocation),
		},
		{
			title: "Expert",
			dataIndex: "expert",
			key: "expert",
			render: (text, record) => renderNullableTag(record.expert, "blue"),
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			render: (text, record) => <p>{renderNullable(record.email)}</p>,
		},
		{
			title: "Mobilization status",
			dataIndex: "mobilization",
			key: "mobilization",
			render: (text, record) => <p>{renderNullable(record.mobilization)}</p>,
		},
		{
			title: "Company",
			dataIndex: "company",
			key: "company",
			render: (text, record) => <p>{renderNullable(record.company)}</p>,
			filters: companyFilters,
			filterMultiple: true,
			filterSearch: true,
			onFilter: (value, record) => record.company === value,
			sorter: (a, b) => compareNullableText(a.company, b.company),
		},
		{
			title: "Role",
			dataIndex: "isAdmin",
			key: "isAdmin",
			render: (isAdmin, record) => {
				const color = record.isAdmin ? "volcano" : "green";
				return <Tag color={color}>{isAdmin ? "Admin" : "Regular"}</Tag>;
			},
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			render: (text, record) => (
				<>
					<span style={{ marginLeft: "10px" }} />
					<div style={{ marginBottom: "2px" }}>
						<Button
							variant="warning"
							size="sm"
							disabled={resettingUserId === record.id}
							onClick={() =>
								handleResetUserPassword(record.id, record.username)
							}
						>
							{resettingUserId === record.id ? "Resetting…" : "Reset password"}
						</Button>
					</div>
					<span style={{ marginLeft: "10px" }} />
					{!record.isAdmin && (
						<div style={{ marginBottom: "2px" }}>
							<Button
								size="sm"
								onClick={() => handleMakeUserAdmin(record.id, record.username)}
							>
								Make Admin
							</Button>
						</div>
					)}
					<span style={{ marginLeft: "10px" }} />
					{record.isAdmin && !record.is_superuser && (
						<div style={{ marginBottom: "2px" }}>
							<Button
								variant="info"
								size="sm"
								onClick={() =>
									handleRevokeUserAdmin(record.id, record.username)
								}
							>
								Revoke Admin
							</Button>
						</div>
					)}
					<span style={{ marginLeft: "10px" }} />
					{
						<div style={{ marginBottom: "2px" }}>
							<Button
								variant="success"
								size="sm"
								onClick={() => showExtractTsModal(record.id, record.username)}
							>
								Extract Timesheet
							</Button>
						</div>
					}

					{user.isAdmin && (
						<>
							<span style={{ marginLeft: "10px" }} />
							{/* Render "Edit User" button if user.isAdmin */}
							<div style={{ marginBottom: "2px" }}>
								<Button
									variant="primary"
									size="sm"
									onClick={() => showEditUserModal(record.id, record.username)}
								>
									Edit User
								</Button>
							</div>
						</>
					)}
				</>
			),
		},
	];

	const MakeUserAdmin = (id) => {
		setAdminUserId(id);
	};

	const RevokeUserAdmin = (id) => {
		setRevokeAdminUserId(id);
	};

	useEffect(() => {
		if (adminUserId !== null) {
			PatchAdminUser(adminUserId);
		}
	}, [adminUserId]);

	useEffect(() => {
		if (revokeAdminUserId !== null) {
			revokeAdmin(revokeAdminUserId);
		}
	}, [revokeAdminUserId]);

	// 誤クリックで対象ユーザーの管理者権限が即座に変わる事故を防ぐため、
	// Reset password と同じく確認ダイアログを1段挟む。PATCH処理自体は不変。
	const handleMakeUserAdmin = (id, username) => {
		Modal.confirm({
			title: "Grant admin access?",
			content: `Grant admin access to "${username}"?`,
			okText: "Grant",
			okType: "primary",
			cancelText: "Cancel",
			onOk: () => MakeUserAdmin(id),
		});
	};

	const handleRevokeUserAdmin = (id, username) => {
		Modal.confirm({
			title: "Revoke admin access?",
			content: `Revoke admin access from "${username}"?`,
			okText: "Revoke",
			okType: "danger",
			cancelText: "Cancel",
			onOk: () => RevokeUserAdmin(id),
		});
	};

	const handleResetUserPassword = (id, username) => {
		// 誤クリックで別ユーザーのパスワードを即座に失わせる事故を防ぐため、
		// 確認ダイアログを1段挟む。リセット処理・結果表示自体は不変。
		//
		// Issue #69: ロックは確認ダイアログを開く前に取る。onOk の中で初めて
		// 立てると、ダイアログが開くまでの間の連打で確認ダイアログが複数開き、
		// それぞれ確定すると同じ利用者のパスワードが何度も再生成される
		// （先に見せた新パスワードが直後に使えなくなる）。別の利用者への
		// 操作は妨げないよう、利用者 ID 単位でロックする。
		if (resetLockRef.current.has(id)) return;
		resetLockRef.current.add(id);
		const releaseResetLock = () => resetLockRef.current.delete(id);
		Modal.confirm({
			title: "Reset password?",
			content: `Reset the password for "${username}"? The user will need to set a new password at next login.`,
			okText: "Reset",
			okType: "danger",
			cancelText: "Cancel",
			// Issue #69: 対象行のボタンを実行中だけ無効にするため、どの利用者を
			// 処理しているかを保持する。Promise を返すので確認ダイアログ側の OK も
			// antd が実行中 loading にする。
			onOk: async () => {
				setResettingUserId(id);
				try {
					await resetUserPassword(id);
				} finally {
					setResettingUserId(null);
					releaseResetLock();
				}
			},
			// antd は onCancel の戻り値が Promise のときに閉じる。同期関数をそのまま
			// 渡すとダイアログが開いたままになるため async にする。
			onCancel: async () => {
				releaseResetLock();
			},
		});
	};

	const editUser = async (id, userData) => {
		try {
			await api.patch(`/user/edit_details/${id}/`, userData);
			getUsers();
			messageApi.success("User details updated successfully");
		} catch (error) {
			messageApi.error("Failed to update user details");
			throw error;
		}
	};

	const resetUserPassword = async (id) => {
		await api
			.post(`/user/reset_user_password/`, {
				userId: id,
			})
			.then((response) => {
				if (response.status === 200) {
					getUsers();
					Modal.success({
						title: "Password Reset",
						content: `New password: ${response.data.password}`,
					});
				}
			})
			.catch((e) => {
				// Error オブジェクトをそのまま渡すと antd message が React の
				// 描画例外を起こしアプリ全体が白画面になる。必ず文字列化して渡す
				messageApi.error(
					e.response?.data?.detail ?? "Failed to reset password",
				);
			});
	};

	const PatchAdminUser = async () => {
		try {
			const response = await api.patch(`/user/make_admin/`, {
				userId: adminUserId,
				isAdmin: true,
			});
			if (response.status === 200) {
				getUsers();
				messageApi.success("You successfully added a new admin");
			} else {
				showError("Failed to make this user an admin");
			}
		} catch (e) {
			showError("Failed to make this user an admin");
		}
	};

	const revokeAdmin = async () => {
		try {
			const response = await api.patch(`/user/revoke_admin/`, {
				userId: revokeAdminUserId,
				isAdmin: false,
			});
			if (response.status === 200) {
				getUsers();
				messageApi.success("You successfully revoked access from an admin");
			} else {
				showError("Failed to revoke access of an admin");
			}
		} catch (e) {
			showError("Failed to revoke access of an admin");
		}
	};

	// サーバー側で拡張子を強制していないため、フロントで .xlsx 以外と
	// 過大サイズのファイルをその場で拒否する(送信自体を行わない)
	// バックエンド(/user/excel_sign_up/)は pd.read_excel を openpyxl/pandas のみで
	// 実行しており .xls 対応の xlrd 依存がないため、.xls は許可しない
	// (旧形式を選ぶと必ず400エラーになる)
	const EXCEL_FILE_EXTENSION_PATTERN = /\.xlsx$/i;
	const EXCEL_FILE_MAX_SIZE_MB = 10;

	const props = {
		name: "file",
		action: `${BASE_URL}/user/excel_sign_up/`,
		accept: ".xlsx",
		headers: {
			authorization: `Bearer ${authTokens.access}`,
		},
		beforeUpload: (file) => {
			if (!EXCEL_FILE_EXTENSION_PATTERN.test(file.name)) {
				message.error(`${file.name} is not an Excel file (.xlsx)`);
				return Upload.LIST_IGNORE;
			}
			if (file.size / 1024 / 1024 >= EXCEL_FILE_MAX_SIZE_MB) {
				message.error(
					`${file.name} exceeds the ${EXCEL_FILE_MAX_SIZE_MB}MB upload limit`,
				);
				return Upload.LIST_IGNORE;
			}
			return true;
		},
		customRequest: async ({ file, onSuccess, onError }) => {
			setExcelUploading(true);
			try {
				await excelSignUp(file);
				onSuccess();
			} catch (error) {
				onError(error);
			} finally {
				setExcelUploading(false);
			}
		},
	};

	// 失敗時は必ず例外を再スロー(または新規スロー)して呼び出し元に伝播させる。
	// catch 内で message.error 表示するだけで握り潰すと、呼び出し元(customRequest)は
	// 常に onSuccess() を呼んでしまい、ユーザー登録が実際には失敗していても
	// Upload 部品が「成功」扱いの表示になる
	const excelSignUp = async (file) => {
		let response;
		try {
			const formData = new FormData();
			formData.append("file", file);

			response = await api.post("user/excel_sign_up/", formData, {
				headers: {
					Authorization: "Bearer " + authTokens.access,
				},
				// timeout を明示しないと応答待ちのまま固まるため上限を置く
				timeout: 120000,
			});
		} catch (error) {
			message.error(
				`Failed to upload ${file.name} due to ${
					error.response?.data?.detail ?? "an unexpected error"
				}`,
			);
			throw error;
		}

		if (response.status !== 200) {
			message.error(`${file.name} file upload failed: ${response.data.detail}`);
			throw new Error(response.data?.detail ?? "Excel upload failed");
		}

		getUsers();

		const createdUserPasswords = response.data.created_user_passwords ?? {};
		const createdCount = Object.keys(createdUserPasswords).length;

		// 全行が既存ユーザーと重複してスキップされたときに気づけるよう、
		// 作成件数を明示する
		if (createdCount > 0) {
			message.success(
				`${file.name} uploaded successfully: ${createdCount} user(s) created`,
			);
			Modal.success({
				title: "Created User Passwords",
				content: (
					<div>
						<p>Please share the following passwords with each user:</p>
						<ul>
							{Object.entries(createdUserPasswords).map(
								([username, password]) => (
									<li key={username}>
										{username}: {password}
									</li>
								),
							)}
						</ul>
					</div>
				),
			});
		} else {
			message.warning("No new users were created (all rows already exist).");
		}
	};

	// 注: /user/get_users/ はサーバーサイドページネーション未対応（全件返却）のため、
	// `page` パラメータは送信しない（antd Table 側の明示的なクライアントサイド
	// ページネーションに統一）。
	const getUsers = async () => {
		setUsersLoading(true);
		try {
			const response = await api.get(`/user/get_users/`);

			if (response.status === 200) {
				const data = response.data;
				setUsers(data);
			} else {
				showError("Failed to load user data. Please try again.");
			}
		} catch (e) {
			// 原因を問わず「権限が無い」と伝えていたため、サーバー障害や
			// ネットワーク断でも利用者が権限問題と誤解していた。403 のときだけ権限文言。
			showError(
				describeRequestError(e, "Failed to load user data. Please try again."),
			);
		} finally {
			// 失敗時もスピナーを残さない（エラー表示のまま回り続けるのを防ぐ）
			setUsersLoading(false);
		}
	};

	// Edit User の Department Select の選択肢。
	// for_selection=1 は応急対応で、空欄名・表記ゆれ重複を除いた名前順の一覧を返す
	// （本番データの重複部門を整理するまでの措置）。選択肢として出さないことで、
	// 表記ゆれ部門を新たに割り当ててしまうことを防ぐ。
	const getDepartments = async () => {
		try {
			const response = await api.get(`/department/`, {
				params: { for_selection: 1 },
			});
			setDepartmentChoices(response.data);
		} catch (e) {
			// 一覧が取れなくても他の編集項目は使えるため、ここでは画面を止めない。
			showError(
				describeRequestError(
					e,
					"Failed to load departments. Please try again.",
				),
			);
		}
	};

	const extract_user_timesheet = async (userId, username) => {
		try {
			let url = `/user/extract_timesheet/${userId}/`;
			if (selectedDate) {
				const formattedDate = `${selectedDate}-01`;
				url += `?date=${formattedDate}`;
			}

			const response = await api.get(url, {
				responseType: "arraybuffer",
			});

			if (response.status === 200) {
				const blob = new Blob([response.data], {
					type: response.headers["content-type"],
				});
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.style.display = "none";
				a.href = url;
				a.download = `${username}_timesheet.xlsx`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				message.success("User activities exported successfully");
			} else {
				showError("Failed to export the timesheet. Please try again.");
			}
		} catch (e) {
			showError(
				describeRequestError(
					e,
					"Failed to export the timesheet. Please try again.",
				),
			);
		} finally {
			setConfirmLoading(false);
			setOpen(false);
		}
	};

	return (
		<div
			style={{
				padding: "10px 5px",
				textAlign: "left",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{contextHolder}
			<div style={{ width: "100%", margin: 10 }}>
				<h1>Users Registered</h1>
			</div>
			<div style={{ width: "100%" }}>
				<Table
					columns={userColumns}
					dataSource={usersDataSource}
					rowKey={(record) => record.id}
					loading={usersLoading}
					pagination={{
						pageSize: usersPageSize,
						showSizeChanger: true,
						pageSizeOptions: [10, 20, 50, 100],
						onShowSizeChange: (_current, size) => setUsersPageSize(size),
						// 全体で何件あり、いま何件目を見ているのかが分からなかった
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
					}}
				/>
			</div>
			<Modal
				title="Upload Excel file"
				open={isExcelModalOpen}
				onCancel={handleCancelExcel}
				// OK ボタンが Cancel と同じ「閉じるだけ」の動作で紛らわしかったため
				// footer を非表示にし、閉じる手段を Cancel/× に一本化する。
				// destroyOnClose で再オープン時に前回アップロードのファイル行が残らないようにする
				footer={null}
				destroyOnClose
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						height: "100%",
					}}
				>
					<Alert
						message="How to use?"
						// 1 文を comma splice で繋いでいた。2 文に分け、
						// 受け付ける拡張子（.xlsx）も明示する。
						description="Upload an Excel file (.xlsx) with two columns: first name and last name. An account is created for every user listed in the file, with the username firstName_lastName and the common initial password."
						type="warning"
					/>
					<div style={{ width: "100%", textAlign: "center", marginTop: "5%" }}>
						<Upload {...props}>
							<Button disabled={excelUploading}>
								<UploadOutlined />{" "}
								{excelUploading ? "Uploading..." : "Click to Upload"}
							</Button>
						</Upload>
					</div>
				</div>
			</Modal>
			{user.isAdmin || user.is_superuser ? (
				<FloatButton.Group shape="circle">
					{user.isAdmin ? (
						<FloatButton
							icon={<UploadOutlined />}
							tooltip={<div>Upload Excel</div>}
							onClick={() => showExcelModal()}
						/>
					) : null}
					<FloatButton
						icon={<CalendarOutlined />}
						tooltip={<div>Working Days Master</div>}
						onClick={() => setIsWorkingDaysModalOpen(true)}
					/>
					<FloatButton
						icon={<ApartmentOutlined />}
						tooltip={<div>Department Management</div>}
						onClick={() => setIsDepartmentModalOpen(true)}
					/>
					{/* 一覧が長くスクロールが前提の画面なので、メニューの最下段に
					    常時置く。スクロール量で出し入れすると上の3つの位置がずれる */}
					<FloatButton.BackTop
						visibilityHeight={0}
						tooltip={<div>Back to top</div>}
					/>
				</FloatButton.Group>
			) : (
				<FloatButton.BackTop tooltip={<div>Back to top</div>} />
			)}
			<WorkingDaysMasterModal
				open={isWorkingDaysModalOpen}
				onClose={() => setIsWorkingDaysModalOpen(false)}
			/>
			<DepartmentManagementModal
				open={isDepartmentModalOpen}
				onClose={() => setIsDepartmentModalOpen(false)}
			/>
			<Modal
				title={editUsername ? `Edit User: ${editUsername}` : "Edit User"}
				open={openEdit}
				onOk={handleEditOk}
				confirmLoading={savingUser}
				cancelButtonProps={{ disabled: savingUser }}
				closable={!savingUser}
				maskClosable={!savingUser}
				onCancel={handleEditCancel}
			>
				<div>
					<label>Grade:</label>
					<Select
						ref={editGradeRef}
						style={{ width: "100%" }}
						placeholder="Select Grade"
						onChange={(value) => handleInputChange("grade", value)}
						value={grade}
					>
						{GRADE_CHOICES.map(({ value, label }) => (
							<Option key={value} value={value}>
								{label}
							</Option>
						))}
					</Select>
				</div>
				<div>
					<label>Organization Code:</label>
					<Input
						value={editedUserData.organizationCode}
						onChange={(e) =>
							handleInputChange("organizationCode", e.target.value)
						}
					/>
				</div>
				<div>
					<label>Position:</label>
					<Input
						value={editedUserData.position}
						onChange={(e) => handleInputChange("position", e.target.value)}
					/>
				</div>
				<div>
					<label>Department:</label>
					{/* 自由入力を廃止し部門マスタ由来の Select にする。
					    マスタに無い値を保存できなくすることで表記ゆれの新規発生源を断つ。
					    allowClear は未所属化に使うが、確認ダイアログの承認を経ないと反映しない。 */}
					<Select
						style={{ width: "100%" }}
						placeholder="Select Department"
						value={editedUserData.dep}
						onChange={handleDepartmentChange}
						allowClear
						showSearch
						optionFilterProp="children"
					>
						{departmentOptions.map((department) => (
							<Option key={department.id} value={department.id}>
								{department.name}
							</Option>
						))}
					</Select>
				</div>
				<div>
					<label>Nat Group:</label>
					<Select
						style={{ width: "100%" }}
						placeholder="Select Nat Group"
						onChange={(value) => handleInputChange("natGroup", value)}
						value={natGroup}
					>
						{NAT_GROUP_CHOICES.map(({ value, label }) => (
							<Option key={value} value={value}>
								{label}
							</Option>
						))}
					</Select>
				</div>
				<div>
					<label>Working Location:</label>
					<Input
						value={editedUserData.workingLocation}
						onChange={(e) =>
							handleInputChange("workingLocation", e.target.value)
						}
					/>
				</div>

				<div>
					<label>Mobilization Status:</label>
					<Input
						value={editedUserData.mobilization}
						onChange={(e) => handleInputChange("mobilization", e.target.value)}
					/>
				</div>
				<div>
					<label>Company:</label>
					<Select
						style={{ width: "100%" }}
						placeholder="Select Company"
						onChange={(value) => handleInputChange("company", value)}
						value={company}
					>
						{COMPANY_CHOICES.map(({ value, label }) => (
							<Option key={value} value={value}>
								{label}
							</Option>
						))}
					</Select>
				</div>
			</Modal>
			<Modal
				title="Extract User's timesheet"
				open={open}
				onOk={() => handleTsOk(extractUserId, extractUsername)}
				confirmLoading={confirmLoading}
				onCancel={handleTsCancel}
			>
				<p>{modalText}</p>
				<DatePicker
					ref={extractDateRef}
					value={selectedDate ? dayjs(selectedDate) : null}
					onChange={handleDateChange}
					picker="month"
				/>
			</Modal>
		</div>
	);
};

export default Dashboard;
